import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  // useRef,
  useState,
} from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { env } from '../configs/env.config'
import { ISignInRequest } from '../sdk/authentication/authentication.types'
import { IError } from '../sdk/base/error'
import { RequestAgentDTO } from '../sdk/user/user.types'
import { sdk } from '../services/sdk'
// import { redirectToUrl } from '../utilities/urls'

interface AuthContextData {
  user: RequestAgentDTO | null
  signOut: () => void
  getUser: () => void
  recoveryPassword: (email: string) => void
  authentication: (email: string, password: string) => Promise<boolean>
  setError: (value: IError | undefined) => void
  error: IError | undefined
  errorForgotPassword: IError | undefined
  userType: string | null
  authLoading: boolean
  recoveryLoading: boolean
  termsError: boolean
  serviceTermsCompanyId?: string
  recoveryResponse: boolean | null
  loginBody: ISignInRequest | null
  headCompanyTermError: boolean
  setHeadCompanyTermError: (value: boolean) => void
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthContext = createContext({} as AuthContextData)
export function AuthProvider(props: AuthProviderProps) {
  const [loginBody, setLoginBody] = useState<ISignInRequest | null>(null)
  const [error, setError] = useState<IError | undefined>(undefined)
  const [errorForgotPassword, setErrorForgotPassword] = useState<
    IError | undefined
  >(undefined)
  const [user, setUser] = useState<RequestAgentDTO | null>(null)
  const [userType, setUserType] = useState<string | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [recoveryLoading, setRecoveryLoading] = useState(false)
  const [recoveryResponse, setRecoveryResponse] = useState<boolean | null>(null)
  const [termsError, setTermsError] = useState<boolean>(false)
  const [serviceTermsCompanyId, setServiceTermsCompanyId] = useState<string>()
  const [headCompanyTermError, setHeadCompanyTermError] = useState(true)

  const navigate = useNavigate()
  const location = useLocation()
  const recoveryPasswordUrl =
    location.pathname.substring(0, 18) === '/password/recovery'
  const cookiesUrl = location.pathname.includes('/cookies')

  useEffect(() => {
    getUser()
  }, [])

  const signOut = useCallback(() => {
    setUser(null)

    localStorage.removeItem(env.LOCAL_STORAGE_LOGIN_TOKEN)

    if (!recoveryPasswordUrl && !cookiesUrl) {
      navigate('/login')

      window.location.reload()
    }
  }, [])

  const authentication = async (
    email: string,
    password: string,
  ): Promise<boolean> => {
    setError(undefined)

    let response: boolean
    setTermsError(false)

    setLoginBody({ email, password })

    try {
      setAuthLoading(true)
      const { token } = await sdk.authentication.signin({
        email,
        password,
      })
      if (token) {
        localStorage.setItem(env.LOCAL_STORAGE_LOGIN_TOKEN, token)
        navigate(0)
      }
      response = true
      setLoginBody(null)
    } catch (error: any) {
      if (
        error.error.message.toLowerCase() ===
        'Usuário precisa aceitar os termos de serviço'.toLowerCase()
      ) {
        setServiceTermsCompanyId(error.error.serviceTermsCompanyId)
        setTermsError(true)
      } else {
        setError(error?.error)
      }
      response = false
      setAuthLoading(false)
    }

    return response
  }

  const getUser = async (): Promise<boolean> => {
    let response: boolean

    try {
      setAuthLoading(true)
      const requestUser = await sdk.user.user()
      setUserType(requestUser.company.segment ?? null)
      setUser(requestUser)
      response = true
    } catch (error) {
      const token = localStorage.getItem(env.LOCAL_STORAGE_LOGIN_TOKEN)

      if (token) {
        signOut()
      }

      response = false
    } finally {
      setAuthLoading(false)
    }

    return response
  }

  const recoveryPassword = async (email: string) => {
    setRecoveryResponse(null)

    try {
      setRecoveryLoading(true)
      setErrorForgotPassword(undefined)
      const recovery = await sdk.user.recoveryPassword(email)
      if (recovery) {
        setError(undefined)
        setRecoveryResponse(recovery)
      }
    } catch (error: any) {
      setErrorForgotPassword(error?.error)
      setRecoveryResponse(false)
    } finally {
      setRecoveryLoading(false)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        signOut,
        recoveryPassword,
        recoveryLoading,
        recoveryResponse,
        authentication,
        getUser,
        error,
        errorForgotPassword,
        setError,
        userType,
        authLoading,
        termsError,
        serviceTermsCompanyId,
        loginBody,
        headCompanyTermError,
        setHeadCompanyTermError,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  )
}
